import React, { Suspense, lazy } from 'react';
import { StoreProvider, useStore } from './context/StoreContext';
import { AnnouncementBar } from './components/AnnouncementBar';
import { Header } from './components/Header';
import { HeroBanner } from './components/HeroBanner';
import { NewsTicker } from './components/NewsTicker';
import { InstagramStories } from './components/InstagramStories';
import { FrontPageShowcase } from './components/FrontPageShowcase';
import { Footer } from './components/Footer';
import { Toast } from './components/Toast';

// Code-split heavy interactive and secondary views for optimal mobile performance
const CustomPrintView = lazy(() => import('./components/CustomPrintView').then(m => ({ default: m.CustomPrintView })));
const AboutAndFAQView = lazy(() => import('./components/AboutAndFAQView').then(m => ({ default: m.AboutAndFAQView })));
const ContactUsView = lazy(() => import('./components/ContactUsView').then(m => ({ default: m.ContactUsView })));
const AdminPortal = lazy(() => import('./components/AdminPortal').then(m => ({ default: m.AdminPortal })));
const SearchConsoleDashboard = lazy(() => import('./components/SearchConsoleDashboard').then(m => ({ default: m.SearchConsoleDashboard })));
const RssFeedGenerator = lazy(() => import('./components/RssFeedGenerator').then(m => ({ default: m.RssFeedGenerator })));
const OffersView = lazy(() => import('./components/OffersView').then(m => ({ default: m.OffersView })));
const BlogView = lazy(() => import('./components/BlogView').then(m => ({ default: m.BlogView })));
const LegalAndPricingView = lazy(() => import('./components/LegalAndPricingView').then(m => ({ default: m.LegalAndPricingView })));
const ProductDetailModal = lazy(() => import('./components/ProductDetailModal').then(m => ({ default: m.ProductDetailModal })));

const ViewLoadingFallback: React.FC = () => {
  const { language } = useStore();
  return (
    <div className="max-w-7xl mx-auto px-4 py-16 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-3 border-[#E21B24] border-t-transparent rounded-full animate-spin" />
        <span className="text-xs uppercase tracking-wider font-bold text-neutral-400">
          {language === 'ar' ? 'جاري تحميل الصفحة...' : 'Loading view...'}
        </span>
      </div>
    </div>
  );
};

const AppContent: React.FC = () => {
  const { currentView, selectedProduct, language, showToast, pagesContent } = useStore();

  // Comprehensive Anti-Theft Image Protection (Desktop & Mobile)
  React.useEffect(() => {
    // 1. Prevent Right-Click / Context Menu on any image or protected container
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      
      const isImg = target.tagName === 'IMG' || target.querySelector('img') !== null;
      const isProtected = target.closest('img') !== null || 
                          target.closest('.image-shield') !== null || 
                          target.closest('[data-product-image]') !== null ||
                          target.closest('[id^="product-card-"]') !== null ||
                          target.closest('#product-detail-modal') !== null ||
                          target.classList.contains('image-shield');

      if (isImg || isProtected) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // 2. Prevent Drag-and-Drop of images to desktop, folder, or other tabs
    const handleDragStart = (e: DragEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.tagName === 'IMG' || target.closest('img') || target.closest('.image-shield')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // 3. Intercept Ctrl+S / Cmd+S save page shortcut
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        showToast(
          language === 'ar'
            ? 'عذراً، تحميل وتخزين محتوى المتجر غير متاح لحماية حقوق الملكية.'
            : 'Saving content is disabled to protect proprietary product catalog.'
        );
      }
    };

    document.addEventListener('contextmenu', handleContextMenu, { capture: true });
    document.addEventListener('dragstart', handleDragStart, { capture: true });
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu, { capture: true });
      document.removeEventListener('dragstart', handleDragStart, { capture: true });
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [language, showToast]);

  return (
    <div className="min-h-screen bg-[#FBFBFB] text-neutral-900 flex flex-col font-sans selection:bg-[#E21B24] selection:text-white">
      {/* 1. Top Announcement Marquee Bar */}
      <AnnouncementBar />

      {/* 2. Global Streetwear Navigation Header */}
      <Header />

      {/* 3. Main Dynamic Content View */}
      <main className="flex-1">
        {currentView === 'home' && (() => {
          const sectionList = pagesContent.sectionOrder && pagesContent.sectionOrder.length > 0
            ? pagesContent.sectionOrder
            : [
                { id: 'hero', enabled: true },
                { id: 'stories', enabled: true },
                { id: 'newsTicker', enabled: true },
                { id: 'showcase', enabled: true }
              ];

          const isHeroEnabled = sectionList.some(sec => sec.id === 'hero' && sec.enabled);

          return (
            <>
              {!isHeroEnabled && (
                <h1 className="sr-only">
                  {language === 'ar'
                    ? 'كفرات موبايل مخصصة، لوحات كانفاس وطباعة حسب الطلب في لبنان — Coveristic'
                    : 'Custom Phone Cases, Canvas Wall Art & Print on Demand in Lebanon — Coveristic'}
                </h1>
              )}
              {sectionList.map(sec => {
                if (!sec.enabled) return null;
                switch (sec.id) {
                  case 'hero':
                    return <HeroBanner key="hero" />;
                  case 'stories':
                    return <InstagramStories key="stories" />;
                  case 'newsTicker':
                    return <NewsTicker key="newsTicker" />;
                  case 'showcase':
                    return <FrontPageShowcase key="showcase" />;
                  default:
                    return null;
                }
              })}
            </>
          );
        })()}

        {currentView === 'shop' && (
          <div className="pt-4">
            <FrontPageShowcase />
          </div>
        )}

        <Suspense fallback={<ViewLoadingFallback />}>
          {currentView === 'custom-print' && (
            <CustomPrintView />
          )}

          {currentView === 'offers' && (
            <OffersView />
          )}

          {currentView === 'about' && (
            <AboutAndFAQView />
          )}

          {currentView === 'contact' && (
            <ContactUsView />
          )}

          {currentView === 'blog' && (
            <BlogView />
          )}

          {(currentView === 'admin' || currentView === 'sitemap') && (
            <AdminPortal />
          )}

          {(currentView === 'search-console' || currentView === 'analytics') && (
            <SearchConsoleDashboard />
          )}

          {currentView === 'rss' && (
            <RssFeedGenerator />
          )}

          {currentView === 'terms' && (
            <LegalAndPricingView mode="terms" />
          )}

          {currentView === 'privacy' && (
            <LegalAndPricingView mode="privacy" />
          )}

          {currentView === 'pricing' && (
            <LegalAndPricingView mode="pricing" />
          )}

          {currentView === '404' && (
            <LegalAndPricingView mode="404" />
          )}
        </Suspense>
      </main>

      {/* 4. Global Product Customization & WhatsApp Forwarding Modal (Loaded on-demand) */}
      {selectedProduct && (
        <Suspense fallback={null}>
          <ProductDetailModal />
        </Suspense>
      )}

      {/* 5. Global Toast Notification */}
      <Toast />

      {/* 6. Footer */}
      <Footer />
    </div>
  );
};

export default function App() {
  return (
    <StoreProvider>
      <AppContent />
    </StoreProvider>
  );
}
